import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Copy,
  Clock,
  Users,
  Gift,
  Percent,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  Timer,
  Ticket,
  Target
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const NewsSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);

  const [newsList, setNewsList] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    link: '',
    link_text: 'Découvrir',
    target_audience: ['all'],
    is_promo: false,
    promo_code: '',
    promo_expires_at: '',
    limited_spots: '',
    is_active: true
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    content: '',
    is_promo: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [newsRes, templatesRes] = await Promise.all([
        api.get('/news/', { params: { active_only: false } }),
        api.get('/news/templates')
      ]);
      setNewsList(newsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...formData,
        limited_spots: formData.limited_spots ? parseInt(formData.limited_spots) : null,
        promo_expires_at: formData.promo_expires_at || null
      };

      if (editingNews) {
        await api.put(`/news/${editingNews.id}`, payload);
        toast.success('Actualité mise à jour !');
      } else {
        await api.post('/news/', payload);
        toast.success('Actualité créée !');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/news/templates', templateForm);
      toast.success('Modèle créé !');
      setShowTemplateModal(false);
      setTemplateForm({ name: '', title: '', content: '', is_promo: false });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('Actualité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleActive = async (news) => {
    try {
      await api.put(`/news/${news.id}`, {
        ...news,
        is_active: !news.is_active
      });
      toast.success(news.is_active ? 'Désactivée' : 'Activée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code promo copié !');
  };

  const editNews = (news) => {
    setEditingNews(news);
    setFormData({
      title: news.title,
      content: news.content,
      link: news.link || '',
      link_text: news.link_text || 'Découvrir',
      target_audience: news.target_audience || ['all'],
      is_promo: news.is_promo || false,
      promo_code: news.promo_code || '',
      promo_expires_at: news.promo_expires_at ? news.promo_expires_at.split('T')[0] : '',
      limited_spots: news.limited_spots?.toString() || '',
      is_active: news.is_active
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setEditingNews(null);
    setFormData({
      title: '',
      content: '',
      link: '',
      link_text: 'Découvrir',
      target_audience: ['all'],
      is_promo: false,
      promo_code: '',
      promo_expires_at: '',
      limited_spots: '',
      is_active: true
    });
  };

  const toggleAudience = (audience) => {
    if (audience === 'all') {
      setFormData({ ...formData, target_audience: ['all'] });
    } else {
      let newAudience = formData.target_audience.filter(a => a !== 'all');
      if (newAudience.includes(audience)) {
        newAudience = newAudience.filter(a => a !== audience);
      } else {
        newAudience.push(audience);
      }
      if (newAudience.length === 0) newAudience = ['all'];
      setFormData({ ...formData, target_audience: newAudience });
    }
  };

  const getAudienceBadges = (audiences) => {
    const labels = {
      all: { label: 'Tout le monde', color: 'bg-gray-100 text-gray-700' },
      clients: { label: 'Clients', color: 'bg-blue-100 text-blue-700' },
      creatives: { label: 'Créatifs', color: 'bg-purple-100 text-purple-700' },
      affiliates: { label: 'Apporteurs', color: 'bg-orange-100 text-orange-700' }
    };
    return audiences.map(a => labels[a] || labels.all);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Actualités & Offres
            </h1>
            <p className="text-[#64748b]">
              Gérez le bandeau d'actualités et les offres promotionnelles
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowTemplateModal(true)}
              variant="outline"
              className="rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Modèle
            </Button>
            <Button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="rounded-full text-white"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle actualité
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Megaphone className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Total actus</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>{newsList.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                  <Eye className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Actives</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {newsList.filter(n => n.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                  <Gift className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Promos</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {newsList.filter(n => n.is_promo).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                  <Ticket className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Modèles</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>{templates.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <Tabs defaultValue="news" className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="news" className="gap-2">
                <Megaphone className="w-4 h-4" />
                Actualités
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-2">
                <Ticket className="w-4 h-4" />
                Modèles
              </TabsTrigger>
            </TabsList>

            {/* News Tab */}
            <TabsContent value="news">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Liste des actualités</CardTitle>
                  <CardDescription>Actualités affichées dans le bandeau du header</CardDescription>
                </CardHeader>
                <CardContent>
                  {newsList.length === 0 ? (
                    <div className="text-center py-12">
                      <Megaphone className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                      <p className="text-[#64748b]">Aucune actualité</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {newsList.map((news) => (
                        <div key={news.id} className={cn(
                          "p-4 rounded-xl border transition-colors",
                          news.is_active ? "border-[#e2e8f0] bg-white" : "border-dashed border-[#cbd5e1] bg-[#f8fafc] opacity-60"
                        )}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="font-medium" style={{ color: theme.colors?.primary }}>{news.title}</h3>
                                {news.is_promo && (
                                  <Badge className="bg-purple-100 text-purple-700">
                                    <Gift className="w-3 h-3 mr-1" />
                                    Promo
                                  </Badge>
                                )}
                                {!news.is_active && (
                                  <Badge className="bg-gray-100 text-gray-700">
                                    <EyeOff className="w-3 h-3 mr-1" />
                                    Désactivée
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-[#64748b] mb-2">{news.content}</p>
                              
                              {/* Audience badges */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Target className="w-3 h-3 text-[#94a3b8]" />
                                {getAudienceBadges(news.target_audience || ['all']).map((badge, i) => (
                                  <Badge key={i} className={badge.color}>{badge.label}</Badge>
                                ))}
                              </div>

                              {/* Promo details */}
                              {news.is_promo && (
                                <div className="flex items-center gap-4 text-xs text-[#64748b] mt-2">
                                  {news.promo_code && (
                                    <button 
                                      onClick={() => copyPromoCode(news.promo_code)}
                                      className="flex items-center gap-1 px-2 py-1 bg-[#f1f5f9] rounded hover:bg-[#e2e8f0]"
                                    >
                                      <Copy className="w-3 h-3" />
                                      Code: <span className="font-mono font-bold">{news.promo_code}</span>
                                    </button>
                                  )}
                                  {news.promo_expires_at && (
                                    <span className="flex items-center gap-1">
                                      <Timer className="w-3 h-3" />
                                      Expire: {news.promo_expires_at.split('T')[0]}
                                    </span>
                                  )}
                                  {news.limited_spots && (
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {news.spots_remaining || news.limited_spots} / {news.limited_spots} places
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                onClick={() => toggleActive(news)}
                              >
                                {news.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                onClick={() => editNews(news)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full text-red-500"
                                onClick={() => handleDelete(news.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Modèles d'actualités</CardTitle>
                  <CardDescription>Modèles pré-remplis pour créer rapidement des actualités</CardDescription>
                </CardHeader>
                <CardContent>
                  {templates.length === 0 ? (
                    <div className="text-center py-12">
                      <Ticket className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                      <p className="text-[#64748b]">Aucun modèle</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {templates.map((template) => (
                        <div key={template.id} className="p-4 rounded-xl border border-[#e2e8f0] hover:border-[#cbd5e1] transition-colors">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium" style={{ color: theme.colors?.primary }}>{template.name}</h4>
                            {template.is_promo && <Badge className="bg-purple-100 text-purple-700">Promo</Badge>}
                          </div>
                          <p className="text-sm text-[#64748b] mb-3">{template.title}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                title: template.title,
                                content: template.content,
                                is_promo: template.is_promo
                              });
                              setShowCreateModal(true);
                            }}
                          >
                            Utiliser ce modèle
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Create/Edit News Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateModal(open); }}>
          <DialogContent className="max-w-xl" aria-describedby="news-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                {editingNews ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
              </DialogTitle>
            </DialogHeader>
            <p id="news-desc" className="sr-only">Formulaire d'actualité</p>
            <form onSubmit={handleCreate} className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nouveauté : Pack Premium..."
                  className="input-default"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contenu</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Description de l'actualité..."
                  className="input-default min-h-[80px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lien</Label>
                  <Input
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="/offers"
                    className="input-default"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texte du lien</Label>
                  <Input
                    value={formData.link_text}
                    onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                    placeholder="Découvrir"
                    className="input-default"
                  />
                </div>
              </div>

              {/* Target Audience */}
              <div className="space-y-2">
                <Label>Cible</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: 'Tout le monde' },
                    { id: 'clients', label: 'Clients' },
                    { id: 'creatives', label: 'Créatifs' },
                    { id: 'affiliates', label: 'Apporteurs' }
                  ].map((audience) => (
                    <button
                      key={audience.id}
                      type="button"
                      onClick={() => toggleAudience(audience.id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm border transition-colors",
                        formData.target_audience.includes(audience.id)
                          ? "border-transparent text-white"
                          : "border-[#e2e8f0] bg-white"
                      )}
                      style={formData.target_audience.includes(audience.id) ? { backgroundColor: theme.colors?.accent } : {}}
                    >
                      {audience.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Promo Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#f8fafc]">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  <span className="font-medium" style={{ color: theme.colors?.primary }}>Offre promotionnelle</span>
                </div>
                <Switch
                  checked={formData.is_promo}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_promo: checked })}
                />
              </div>

              {/* Promo Options */}
              {formData.is_promo && (
                <div className="space-y-4 p-4 rounded-xl bg-purple-50 border border-purple-100">
                  <div className="space-y-2">
                    <Label>Code promo (copiable)</Label>
                    <Input
                      value={formData.promo_code}
                      onChange={(e) => setFormData({ ...formData, promo_code: e.target.value.toUpperCase() })}
                      placeholder="PROMO2024"
                      className="input-default font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date d'expiration</Label>
                      <Input
                        type="datetime-local"
                        value={formData.promo_expires_at}
                        onChange={(e) => setFormData({ ...formData, promo_expires_at: e.target.value })}
                        className="input-default"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Places limitées</Label>
                      <Input
                        type="number"
                        value={formData.limited_spots}
                        onChange={(e) => setFormData({ ...formData, limited_spots: e.target.value })}
                        placeholder="10"
                        className="input-default"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#f8fafc]">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-green-600" />
                  <span className="font-medium" style={{ color: theme.colors?.primary }}>Activer immédiatement</span>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { resetForm(); setShowCreateModal(false); }} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingNews ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Template Modal */}
        <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
          <DialogContent aria-describedby="template-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouveau modèle</DialogTitle>
            </DialogHeader>
            <p id="template-desc" className="sr-only">Créer un modèle d'actualité</p>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du modèle *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="Pack Premium"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Titre de l'actualité *</Label>
                <Input
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                  placeholder="Offre spéciale : Pack Premium..."
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Contenu</Label>
                <Textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  placeholder="Description..."
                  className="input-default"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={templateForm.is_promo}
                  onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_promo: checked })}
                />
                <Label>Modèle promo</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowTemplateModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default NewsSettingsPage;
